import { IGRatingValues } from '../filter/types'
import { Order } from '../order/types'
import {
  QuoteRequest,
  RawQuoteRequest,
  RawRFQQuote,
  RfqEntryModel,
  RFQQuote
} from './types'

type ObjectShape<O extends object> = {
  [K in keyof O]: string
}
// typescript assertion functions have to throw when the result is not valid, otherwise I'd directly return err
function validateAllServerProps<
  T extends object,
  S = ObjectShape<T>,
  K extends keyof T = keyof T
>(serverResponse: T, shape: S): asserts serverResponse is Required<T> {
  // TS does not believe shape is an actual object
  // @ts-ignore
  const shapeArr = Object.entries(shape) as Array<[K, T[K]]>
  if (
    shapeArr.some(([prop, type]) => {
      const serverResponseValue = serverResponse[prop]
      if (type === 'array') {
        if (
          !serverResponseValue ||
          serverResponseValue.constructor.name !== 'Array'
        ) {
          return true
        }
      } else if (typeof serverResponseValue !== type) {
        return true
      }
    })
  ) {
    throw new Error('Incomplete server response')
  }
}
const errValue = '#ERR#'
export const rawQuoteShape = {
  id: 'number',
  price: 'number',
  expireTime: 'string',
  isLive: 'boolean',
  isFirm: 'boolean',
  status: 'string',
  markedPrice: 'number',
  markedSpread: 'number'
} as const
export const rfqQuoteResponseToRfqQuote = (
  rawQuote: Partial<RawRFQQuote>,
  serverTimeOffset: number = 0
): RFQQuote | '#ERR#' => {
  try {
    validateAllServerProps(rawQuote, rawQuoteShape)
  } catch (err) {
    return errValue
  }
  const {
    id,
    price,
    expireTime: expireTimeStr,
    isLive,
    isFirm,
    status,
    markedPrice,
    markedSpread,
    firmName,
    traderName
  } = rawQuote
  const expireTime = new Date(expireTimeStr)
  if (isNaN(expireTime.valueOf())) {
    return errValue
  }
  return {
    id,
    price,
    expireTime: new Date(expireTime.valueOf() - serverTimeOffset),
    isLive,
    isFirm,
    status,
    markedPrice,
    markedSpread,
    firmName,
    traderName
  }
}

export const requestShape = {
  securityId: 'number',
  requestId: 'number',
  orderId: 'string',
  isSpread: 'boolean',
  bidWanted: 'boolean',
  size: 'number',
  boardLabel: 'string',
  isin: 'string',
  benchmark: 'string',
  initiator: 'string',
  currency: 'string',
  isTreasury: 'boolean',
  quotes: 'array',
  bestResponse: 'object',
  isLive: 'boolean',
  isExpired: 'boolean',
  discloseMyName: 'boolean',
  exposureDuration: 'number',
  isASAP: 'boolean'
} as const
export const rfqQuoteRequestResponseToRfqQuoteRequest = (
  rawRequest: Partial<RawQuoteRequest>,
  serverTimeOffset: number = 0
): QuoteRequest | '#ERR#' => {
  try {
    validateAllServerProps(rawRequest, requestShape)
  } catch (e) {
    return errValue
  }
  const {
    securityId,
    requestId,
    orderId,
    isSpread,
    bidWanted,
    size,
    price,
    boardLabel,
    isin,
    benchmark,
    initiator,
    currency,
    isTreasury,
    quotes: rawQuotes,
    bestResponse,
    isLive,
    isExpired,
    discloseMyName,
    exposureDuration,
    hedge,
    isASAP,
    status
  } = rawRequest
  const quotes = rawQuotes
    .map((q) => rfqQuoteResponseToRfqQuote(q, serverTimeOffset))
    .filter((q) => q !== errValue)

  const updatedBestResponse = rfqQuoteResponseToRfqQuote(
    bestResponse,
    serverTimeOffset
  )
  return {
    securityId,
    requestId,
    orderId,
    isSpread,
    type: bidWanted ? 'sell' : 'buy',
    size,
    price,
    boardLabel,
    isin,
    benchmark,
    initiator,
    currency,
    isTreasury,
    quotes,
    bestResponse:
      updatedBestResponse !== errValue ? updatedBestResponse : undefined,
    isLive,
    isExpired,
    discloseMyName,
    exposureDuration,
    hedge,
    isASAP,
    status
  }
}

export const getSpreadEligibility = (rating: string, spread?: number) => {
  return IGRatingValues.includes(rating) && spread !== undefined
}

export const createRfqEntryModel = (
  order: Order,
  exposureDurationDefault: number
): RfqEntryModel => {
  const isSpread = getSpreadEligibility(order.rating!, order.spread)
  return {
    securityId: order.securityId,
    originalOrderId: order.id,
    isSpread,
    size: '',
    price: '',
    type: order.type,
    discloseMyName: false,
    isASAP: true,
    hedge: 'Outright',
    exposureDuration: exposureDurationDefault
  }
}

export const rfQEntryModelToServerQuoteRequest = (rfq: RfqEntryModel) => {
  const price = rfq.price ? Number(rfq.price) : undefined
  const dropIfUndefined = { price, hedge: rfq.hedge }
  const { size, hedge, price: skippedPrice, ...rest } = rfq
  return { ...rest, ...dropIfUndefined, size: Number(size) }
}
